import React, { useState, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import Button from '../ui/button';
//import Input from '../ui/input';
//import Label from '../ui/label';
import { CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import TipSelector from '../ui/tipselector';
import Toggle from '../ui/toggle';
import { calculateCartTotal, detectMetadataChanges } from "../../utils/checkoutUtil";
import { InitiatePayment } from "../../services/stripeTerminal";
import { useSelector } from 'react-redux';
import { useOrder } from "../../Hooks/useOrder";
import PaymentModal from "../ui/paymentModal";
import PaymentModalWrapper from "../ui/paymentModalWrapper";
import CardErrorModal from "../ui/cardErrorModal";
import PhoneNumberScreen from './customerDetail';
import OrderUpdateModal from '../ui/OrderUpdateModal';
import { useRefreshToken } from "../../Hooks/useRefreshToken";
import { store } from "../../redux/store";
import { isWrapperMode } from "../../services/wrapperService";

export default function Checkout({ restaurant_info, cart, cartTotal, orderType, onComplete, onBack, onCancel, onApplyUpdates, cancelSource, setCancelSource }) {

  // check token validity
  const { refreshToken } = useRefreshToken();
  let currentToken = store.getState().auth.accessToken;
  const currentTokenExpiresAt = store.getState().auth.expiresAt * 1000;
  const refreshTokenSaved = store.getState().auth.refreshToken;
  const userName = store.getState().auth.userProfile.username;
  if (Date.now() >= currentTokenExpiresAt) {
    // if accesstoken expires , call refresh token
    const refreshTokenData = refreshToken(refreshTokenSaved, userName);
    currentToken = refreshTokenData.accessToken;  // change access token
  }

  const basePayloadRef = useRef(null);
  const [orderTypeSelected, setOrderTypeSelected] = useState(orderType);
  const [step, setStep] = useState('paynow'); // receipt, payment, paynow, processing
  const [showPay, setShowPay] = useState(null);
  const [showCardError, setShowCardError] = useState(null);
  const [cardError, setCardError] = useState(null);
  // const [wantsReceipt, setWantsReceipt] = useState(null);
  const [name, setName] = useState('');
  // const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [tipPercent, setTipPercent] = useState({ value: 0, type: '' });
  const taxinfo = restaurant_info.tax;
  const restaurant_id = restaurant_info.id;
  const serviceFee = restaurant_info.service_fee;
  const bulkOrderFee = restaurant_info.bulk_order_fees;
  const tipsArray = restaurant_info.tips;
  const tipsValue = tipsArray.regular.pickup.values;
  const tipsType = tipsArray.regular.pickup.type;

  const restaurantMetadata = useSelector((state) => state.restaurantMetadata.data);
  const [openOrderUpdateModal, setOpenOrderUpdateModal] = useState({ status: false, changes: null });
  const { submitOrder } = useOrder();
  const totals = useMemo(
    () => calculateCartTotal(cartTotal, tipPercent.value, tipPercent.type, taxinfo, serviceFee, bulkOrderFee),
    [cartTotal, tipPercent.value, tipPercent.type, taxinfo, serviceFee, bulkOrderFee]
  );
  // Build base payload whenever totals/cart/orderType change
  const basePayload = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const initialPayload = {
      user: {
        name: name,
        phone: phone,
        country_code: "+1",
        email: ""
      },
      restaurant: {
        id: restaurant_info.id,
        type: "regular"
      },
      order: {
        type: orderTypeSelected,
        order_date_time: now,
        schedule_type: "asap",
        tips: totals.tip_amountRaw,
        tax: totals.taxAmountRaw,
        service_fee: totals.service_FeeRaw,
        total_menu_price: cartTotal.toFixed(2),
        grand_total: totals.totalRaw,
        bulk_fee: totals.bulkFeeRaw,
        instruction: ""
      },
      menu: {
        items: cart.map(item => ({
          id: item.item_id,
          category_id: item.item_category_id,
          title: item.item_name,
          quantity: item.quantity,
          price: Number(item.item_base_price).toFixed(2),
          instruction: item.instructions,
          totalPrice: item.subtotal.toFixed(2),
          addOn: item.customizations?.map(c => ({
            no: c.groupId,
            name: c.name,
            price: c.price.toFixed(2),
            qty: String(c.qty ?? 1),
            option_id: c.optionId,
            opt_id: c.option_id,
            isSelected: true,
            unit: "",
            submodifiers: c.subModifiers?.map(sub => ({
              no: sub.groupId,
              name: sub.name,
              price: sub.price.toFixed(2),
              qty: String(sub.qty ?? 1),
              option_id: sub.optionId,
              opt_id: sub.option_id,
              isSelected: true,
              unit: ""
            })) || []
          })) || []
        }))
      }
    };
    // console.log("PAYLOAD BUILT", initialPayload);
    basePayloadRef.current = initialPayload;
    return initialPayload;

  }, [cart, cartTotal, orderTypeSelected, restaurant_info, totals, tipPercent.value, tipPercent.type, name, phone]);
  // send order to wrapper 
  useEffect(() => {
    if (showPay && isWrapperMode() === "yes") {
      const amount_sent = (Number(totals.totalRaw) * 100).toFixed(2)
      console.log("amount_sent", amount_sent)
      const messageData = {
        type: "PAYMENT_EVENT",
        showPay: true,
        accessToken: currentToken,
        restId: restaurant_info.id,
        amount: Number(amount_sent), //in cents
        currency: "USD",
      };
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify(messageData));
        console.log("Sent PAYMENT_EVENT to wrapper:", messageData);
      } else {
        console.warn("ReactNativeWebView not found while in wrapper mode");
      }
    }
  }, [showPay, restaurant_info.id, totals.total]);
  // Handler functions

  const handleOrderType = (selectedType) => {
    setOrderTypeSelected(selectedType);
  };

  const handlepay = () => {
    setStep('receipt');
  };

  const closePhoneNumberScreen = (name, phone) => {
    setName(name);
    setPhone(phone);
    setStep("paynow");
  };

  const handleDetails = ({ name, phone }) => {
    console.log("Details received:", name, phone);
    setName(name);
    setPhone(phone);

    // Check for metadata changes
    const diff = detectMetadataChanges(cart, restaurantMetadata, restaurant_info);

    // If no changes at all, just proceed to payment screen
    if (!diff) {
      setShowPay(true);
      setStep("paynow");
      return;
    }

    // if the difference has any change we show the popup
    let hasSignificantUpdates = false;
    if (diff.itemsUpdated?.length > 0) {
      hasSignificantUpdates = diff.itemsUpdated.some(item =>
        item.priceChanged ||
        item.removedModifiers.length > 0 ||
        item.modifierPriceChanges.length > 0 ||
        item.modifierQtyChanges.length > 0 ||
        item.modifierNameChanges.length > 0
      );
    }

    const isSignificant =
      diff.itemsRemoved?.length > 0 ||
      hasSignificantUpdates ||
      diff.restaurantInfoUpdated?.length > 0 ||
      diff.totalsUpdated !== null;

    if (isSignificant) {
      setOpenOrderUpdateModal({ status: true, changes: diff });
    } else {
      // If there is only name change dont show modal and update cart
      if (diff.itemsUpdated?.length > 0) {
        onApplyUpdates?.(diff);
      }
      setShowPay(true);
      // close the phone number screen
      setStep("paynow");
    }
  };

  const handleContinueAfterUpdates = () => {
    const currentDiff = openOrderUpdateModal.changes;
    setOpenOrderUpdateModal({ status: false, changes: null });
    onApplyUpdates(currentDiff);
    setShowPay(true);
  }

  const handleCloseOrderUpdateModal = () => {
    const currentDiff = openOrderUpdateModal.changes;
    setOpenOrderUpdateModal({ status: false, changes: null })
    onApplyUpdates(currentDiff);
    setStep("paynow");
  }


  const onAutoCanel = async () => {
    setShowPay(false);
    setCancelSource('autoCancel');
    onCancel('autoCancel');
    console.log("cancelled order and returning to home screen");

  }
  const handlePaymentFailure = async (err) => {
    setCardError(err);
    setShowPay(false);
    setShowCardError(true);
    setStep('paynow');
    //console.error("Payment failed:", JSON.stringify(err));
    {/* wait and show insert card again */ }
    // setTimeout(() => {
    //   setShowCardError(false);
    //   setStep('paynow');
    // }, 3000); //wait 3 sec
  };
  const handlePaymentSuccess = async (paymentIntent) => {
    setStep('processing');

    //  Always normalize to ID only
    const paymentIntentId =
      typeof paymentIntent === 'string'
        ? paymentIntent
        : paymentIntent?.id;

    //  Build final payload
    const payload = {
      ...basePayloadRef.current,
      payment: {
        method: "stripe",
        type: "terminal",
        status: "success",
        success: {
          paymentIntent: paymentIntentId
        }
      }
    };


    console.log(
      "%c FINAL ORDER PAYLOAD (Sent to Backend)",
      "color: green; font-weight: bold; font-size: 14px;"
    );
    console.log(JSON.stringify(payload, null, 2));

    try {
      const res = await submitOrder(payload);
      const now_time = moment().format('MMM D, h:mm A');
      console.log('RESPONSE FROM ORDER API', res)
      const tipVal = totals.tip_amountRaw;
      const taxVal = (Number(totals.taxAmountRaw) + Number(totals.service_FeeRaw)).toFixed(2);

      // build payload for receipt
      const receiptPayload = {
        receipt: {
          order_type: orderTypeSelected === 'dinein' ? 'Dine-In' : 'Take-Out',
          order_number: res.data.order.kiosk_order_id,
          total_items: String(cart.length),
          subtotal: String(cartTotal.toFixed(2)),
          ...(Number(tipVal) > 0 && { tip: String(tipVal) }),
          ...(Number(taxVal) > 0 && { tax: String(taxVal) }),
          total: String(totals.totalRaw),
          items: cart.map(item => ({
            qty: String(item.quantity),
            name: item.item_name,
            price: String(item.subtotal.toFixed(2)),
            options: item.customizations?.map(c => ({
              name: c.name,
              sub_options: c.subModifiers?.map(sm => sm.name) || []
            })) || [],
          })),
          order_placed_at: now_time,
          payment_status: "PAID"
        }
      }
      setStep("completed");
      onComplete(res, receiptPayload);
    } catch (err) {
      console.error("Order API Error:", err);
      setShowPay(false);
      onComplete(null); // to show order failure screen
    }
  };



  // Page UI
  return (
    <div className="bg-bordergray">
      <div className=" relative mx-3 md:m-10 md:h-[85vh] lg:h-[85vh] h-[90vh] flex flex-col bg-white ">
        {/* Header */}
        <div className="bg-white text-textblack flex flex-row items-center justify-between px-6 ">
          <div className="w-full flex items-center justify-between sm:contents">
            {/* Back button */}
            <div className="order-1 flex justify-start ">
              {step !== 'processing' ? (
                <Button
                  variant="default"
                  size="back"
                  onClick={onBack}
                  disabled={isProcessing}
                  className="bg-black"
                >
                  <img
                    src="/images/back.png"
                    alt="back"
                    className=" md:w-[70px] md:h-[70px] sm:w-[60px] sm:h-[60px] w-[40px] h-[40px] rounded-[12px]"
                  />
                </Button>
              ) : (
                <div className="hidden sm:block text-lg lg:text-[24px] font-outfit font-medium font-weight-500 text-boonsOrange">
                  Order Type: {orderTypeSelected === "dinein" ? "Dine-In" : "Take-Out"}
                </div>
              )}
              <span className="mt-1  ml-2  font-outfit font-semibold text-textcolor text-[30px] md:text-[52px] lg:text-[64px]">
                Checkout
              </span>
            </div>
          </div>

          <div className="order-3  w-1/3 flex justify-center ">
            {step !== 'processing' ? (
              <div className="lg:mr-20 md:mr-15 mr-5 scale-75 origin-center">
                <Toggle orderTypeDefault={orderType} onOrderTypeSelect={handleOrderType} />
              </div>
            ) : (
              <div className="sm:hidden text-lg font-outfit font-medium font-weight-500 text-boonsOrange">
                Order Type: {orderTypeSelected === "dinein" ? "Dine-In" : "Take-Out"}
              </div>
            )}
          </div>
        </div>



        <div className="flex-1 overflow-y-auto p-4">

          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-t-2xl p-4">
            <div className='flex justify-between items-center mb-4 lg:mb-8'>
              <div className="font-outfit font-medium text-textgray text-xl md:text-[30px] lg:text-[38px]">LEAVE A TIP</div>
              <div className="font-outfit font-medium text-textcolor text-xl md:text-[30px] lg:text-[38px]">
                <span>${totals.tip_amount}</span>
              </div>
            </div>
            <div className='mb-8'><TipSelector options={tipsValue} optionType={tipsType} onTipSelect={(tipData) => setTipPercent(tipData)} /></div>
            <div className="border-b-2 border-bordergray"></div>
            <div className="flex flex-col sm:flex-row text-left justify-between border-bordergray pt-4 pb-2 gap-4">
              <div className="text-textprimary font-outfit font-medium text-xl lg:text-[32px]  ml-auto">
                <div className="flex justify-between sm:grid sm:grid-cols-2">
                  <span className="font-outfit text-textcolor font-medium md:text-[36px] text-[20px] mr-4 lg:mr-10 md:p-5">Subtotal</span>
                  <span className="font-outfit text-textcolor font-medium md:text-[36px] text-[20px] md:p-5 text-right">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between sm:grid sm:grid-cols-2">
                  <span className="font-outfit text-textcolor font-medium md:text-[36px] text-[20px] mr-4 lg:mr-10 md:p-5">Tip</span>
                  <span className="font-outfit text-textcolor font-medium md:text-[36px] text-[20px] md:p-5 text-right">${totals.tip_amount}</span>
                </div>
                <div className="flex justify-right sm:grid sm:grid-cols-2">
                  <span className="font-outfit text-textcolor font-medium md:text-[36px] text-[20px] mr-4 lg:mr-10 md:p-5">Tax & Fees</span>
                  <span className="font-outfit text-textcolor font-medium md:text-[36px] text-[20px] md:p-5 text-right">${totals.taxAmount}</span>
                </div>

              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-b-2xl mb-1 md:p-5">
            <div className="border-b-2 border-bordergray"></div>
            {/* <div className="flex flex-col sm:flex-row text-left justify-between border-bordergray md:pt-4 pb-4 "> */}
            <div className="flex sm:flex-row text-left justify-end text-textprimary font-outfit font-medium text-xl lg:text-[32px] ml-auto">
              <div className="flex justify-between sm:grid sm:grid-cols-2 ">
                <span className="font-outfit text-textcolor font-bold text-[15px] md:text-[42px] lg:text-[50px] md:pt-5 p-4 whitespace-nowrap">Order Total</span>
                <span className="font-outfit text-textcolor font-bold text-[20px] md:text-[40px] md:p-5 p-4 text-right">${totals.total}</span>
              </div>
            </div>
          </div>
          {/* </div> */}

          {/* Order Summary */}
          <div className="bg-white rounded-2xl mb-8">
            <div className="flex justify-center font-outfit font-bold text-boonsOrange text-[40px] md:text-[40px] lg:text-[60px] p-5 mb-1">Order Summary</div>
            <div className="space-y-2">
              {cart.map((item, idx) => (
                <div key={idx} className="border-bordergray border-2 md:py-4 md:px-4 py-2 px-2 rounded-[14.5px] flex flex-row gap-4">
                  {/* image */}
                  <div className="flex-shrink-0 flex justify-center">
                    <img
                      src={item.item_image}
                      alt={item.item_name}
                      className="lg:w-[120px] lg:h-[120px] md:w-[80px] md:h-[80px] w-[60px] h-[60px]  aspect-square object-cover rounded-[10px]"
                    />
                  </div>

                  {/* details */}
                  <div className='flex flex-col flex-1 min-w-0'>
                    <div className="flex md:ml-4 justify-between items-start md:gap-2 gap-0 md:p-5 p-2">
                      <span className="text-black font-outfit font-medium text-md md:text-3xl lg:text-4xl line-clamp-2">
                        {item.quantity}x {item.item_name}
                      </span>
                      <span className="text-black font-outfit font-medium text-md md:text-3xl lg:text-4xl whitespace-nowrap">
                        ${item.subtotal.toFixed(2)}
                      </span>
                    </div>

                    {/* Customizations */}
                    {item.customizations && item.customizations.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {item.customizations.map((custom, cIdx) => (
                          <div key={cIdx} className="md:ml-10 ml-2 text-sm md:text-2xl lg:text-[20px] text-textgray font-outfit font-medium">
                            <ul className="list-disc marker:text-boonsOrange text-xs md:text-2xl lg:text-[20px] space-y-0.5">
                              <li key={cIdx} className="break-words">
                                {custom.qty} X {custom.name.charAt(0).toUpperCase() + custom.name.slice(1)}
                                {/* {cIdx < item.customizations.length - 1 && ' , '} */}
                                {custom.price > 0 && (
                                  <span className="text-boonsOrange">
                                    {" "}(${custom.price.toFixed(2)} )
                                  </span>
                                )}
                              </li>
                            </ul>
                            {custom.subModifiers && custom.subModifiers.length > 0 && (
                              <div className="mt-1 ml-2 border-boonsOrange/30 pl-2">
                                {/* <ul className="list-disc text-xs md:text-2xl lg:text-[20px] space-y-0.5"> */}
                                {custom.subModifiers.map((sub, subIdx) => (
                                  <div key={subIdx}>
                                    {sub.qty} X {sub.name}
                                    {sub.price > 0 && (
                                      <span className="text-boonsOrange">
                                        {" "}(${sub.price.toFixed(2)})
                                      </span>
                                    )}
                                  </div>
                                ))}
                                {/* </ul> */}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* </div> */}

        </div>

        {/* footer */}
        <div className=''>
          {/* {step === 'paynow' && ( */}
          <div className="fixed border-t-2 border-[2px] border-gray-200 bottom-0 left-0 right-0 z-50 flex items-center justify-between  bg-bordergray  pl-3 md:h-[100px] h-[40px] lg:h-[180px]">
            {/* Edit order */}
            {/* <div className="flex-1 flex items-center justify-center"> */}
            <div className="flex-1 flex items-center justify-center " onClick={onBack}>
              <Button
                size="add"
                variant="default"
                // onClick={onBack}
                className="md:h-[100px] h-[40px] lg:h-[180px] text-textgray font-outfit font-medium leading-[1.2] text-center  whitespace-normal disabled:opacity-50">
                Edit My Order
              </Button>
            </div>
            {/* </div> */}
            <div className="flex-1 bg-boonsOrange flex items-center justify-center" onClick={() => handlepay()}>
              <Button
                size="add"
                variant='orange'
                // onClick={() => handlepay()}
                className="bg-boonsOrange md:h-[100px] h-[40px] lg:h-[180px] text-white  font-outfit font-medium leading-[1.2] text-center  whitespace-normal disabled:opacity-50"
              >
                Pay Now
              </Button>
            </div>
          </div>
          {/* )} */}
          {/* receipt */}
          {step === 'receipt' && (
            <AnimatePresence>
              <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closePhoneNumberScreen} // click outside closes
              >
                <motion.div
                  initial={{ scale: 0.9, y: 30, opacity: 0 }}
                  animate={{ scale: 1, y: 0, opacity: 1 }}
                  exit={{ scale: 0.9, y: 30, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  onClick={(e) => e.stopPropagation()} // prevent close on click inside
                  className="bg-white rounded-2xl w-[95vw] max-w-[900px] p-6"
                >
                  <PhoneNumberScreen
                    onClose={closePhoneNumberScreen}
                    onContinue={handleDetails}
                    extdetails={name}
                  />
                </motion.div>
              </motion.div>
            </AnimatePresence>
          )}

          {/* Payment */}
          {step === 'payment' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-12 h-12 text-orange-500" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Ready to Pay</h2>
                <p className="text-xl text-gray-600 mb-8">
                  Please tap your card on the card reader to complete your payment
                </p>
              </div>

              <Button
                size="lg"
                variant="orange"
                // onClick={handlePayment}
                className="w-full py-8 text-[32px] font-outfitmedium font-bold bg-boonsOrange"
              >
                Process Payment - ${totals.total}
              </Button>
            </motion.div>
          )}

          {/* Processing */}
          {step === 'processing' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-boonsOrange mb-8"></div>
                <p className="text-white text-4xl font-medium">
                  Processing Payment...
                </p>
                <p className="text-xl text-gray-600">Please wait, do not remove your card</p>
              </div>
            </div>
          )}
          {/* Payment successful */}
          {step === 'completed' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Payment successful..</h2>
              <p className="text-xl text-gray-600">Please remove your card.</p>
              <p className='text-xl text-gray-600'>Your Order Number is 189</p>
            </motion.div>
          )}
        </div>
        <div>

        </div>
        <div
          className="absolute bottom-[-1px] left-0 h-4 w-full"
          style={{
            background: '#D9D9D9',
            maskImage: 'radial-gradient(circle at 10px 0%, transparent 7px, black 7px)',
            WebkitMaskImage: 'radial-gradient(circle at 10px 0%, transparent 7px, black 7px)',
            maskSize: '25px 100%',
            WebkitMaskSize: '25px 100%',
            maskRepeat: 'repeat-x',
            WebkitMaskRepeat: 'repeat-x'
          }}
        />
      </div>
      {/* show insert card modal */}
      {showPay && isWrapperMode() === "no" && (
        <PaymentModal
          isOpen={showPay}
          onClose={() => setShowPay(false)}
          onAutoCanel={onAutoCanel}
          total={totals.totalRaw}
          accessToken={currentToken}
          restaurantId={restaurant_id}
          InitiatePayment={InitiatePayment}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFailure={handlePaymentFailure}
        // handleProcessing={handlePayment}
        />
      )}
      {showPay && isWrapperMode() === "yes" && (
        <PaymentModalWrapper
          isOpen={showPay}
          onClose={() => setShowPay(false)}
          onAutoCancel={onAutoCanel}
          total={totals.total}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFailure={handlePaymentFailure}
        />
      )}
      {/* show Card Error Modal */}
      {showCardError && (
        <CardErrorModal
          isOpen={showCardError}
          error={cardError}
          onClose={() => setShowCardError(false)}
        />
      )}

      {/* order updated popup */}
      {openOrderUpdateModal && (
        <OrderUpdateModal
          open={openOrderUpdateModal.status}
          onClose={handleCloseOrderUpdateModal}
          changes={openOrderUpdateModal.changes}
          onSubmit={handleContinueAfterUpdates}
        />)}

    </div>

  );
}